/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Resize Transform Node
   GPU-accelerated image resizing with Fit, Fill, and Exact modes.
   ═══════════════════════════════════════════════════════════════ */

import type { NodeDefinition } from '../types';
import { GLSL_HEADER } from '../../rendering/effects/glslCommon';
import { createShaderProcessor } from '../../rendering/effects/shaderProcessor';
import { getMediaDimensions } from '../../rendering/media';

const RESIZE_FRAGMENT_SHADER = `${GLSL_HEADER}
uniform sampler2D u_image;
uniform vec2 u_srcResolution;
uniform vec2 u_targetResolution;
uniform int u_mode; // 0 = Exact, 1 = Fit, 2 = Fill

void main() {
  vec2 uv = v_uv;

  if (u_mode == 1) { // Fit
    float srcAspect = u_srcResolution.x / u_srcResolution.y;
    float targetAspect = u_targetResolution.x / u_targetResolution.y;

    if (srcAspect > targetAspect) {
      // Letterbox top and bottom
      float scale = targetAspect / srcAspect;
      float offset = (1.0 - scale) * 0.5;
      float mappedY = (uv.y - offset) / scale;
      if (mappedY < 0.0 || mappedY > 1.0) {
        fragColor = vec4(0.0);
        return;
      }
      uv.y = mappedY;
    } else {
      // Pillarbox left and right
      float scale = srcAspect / targetAspect;
      float offset = (1.0 - scale) * 0.5;
      float mappedX = (uv.x - offset) / scale;
      if (mappedX < 0.0 || mappedX > 1.0) {
        fragColor = vec4(0.0);
        return;
      }
      uv.x = mappedX;
    }
  } else if (u_mode == 2) { // Fill (Crop overflow)
    float srcAspect = u_srcResolution.x / u_srcResolution.y;
    float targetAspect = u_targetResolution.x / u_targetResolution.y;

    if (srcAspect > targetAspect) {
      float scale = srcAspect / targetAspect;
      float offset = (1.0 - 1.0 / scale) * 0.5;
      uv.x = uv.x / scale + offset;
    } else {
      float scale = targetAspect / srcAspect;
      float offset = (1.0 - 1.0 / scale) * 0.5;
      uv.y = uv.y / scale + offset;
    }
  }

  fragColor = texture(u_image, uv);
}
`;

export const resizeNode: NodeDefinition = {
  type: 'transform.resize',
  category: 'Transform',
  label: 'Resize',
  description: 'Scales images with aspect-ratio preserving Fit, Fill, or Exact modes',
  keywords: ['scale', 'resize', 'fit', 'fill', 'resolution', 'aspect'],
  targetBackend: 'webgl2',
  inputs: [
    { id: 'image', name: 'Source', type: 'image' },
  ],
  outputs: [
    { id: 'output', name: 'Resized', type: 'image' },
  ],
  params: [
    {
      id: 'mode',
      name: 'Scaling Mode',
      type: 'select',
      defaultValue: 'fit',
      options: [
        { label: 'Fit (Letterbox)', value: 'fit' },
        { label: 'Fill (Crop)', value: 'fill' },
        { label: 'Exact (Stretch)', value: 'exact' },
      ],
    },
    {
      id: 'width',
      name: 'Width',
      type: 'number',
      defaultValue: 1024,
      min: 16,
      max: 8192,
      step: 1,
      unit: 'px',
    },
    {
      id: 'height',
      name: 'Height',
      type: 'number',
      defaultValue: 1024,
      min: 16,
      max: 8192,
      step: 1,
      unit: 'px',
    },
  ],
  processor: createShaderProcessor({
    fragmentShader: RESIZE_FRAGMENT_SHADER,
    calculateDimensions: (_inputs, params) => {
      const targetW = Number(params.width) || 1024;
      const targetH = Number(params.height) || 1024;
      return { width: targetW, height: targetH };
    },
    getUniforms: (inputs, params, dimensions) => {
      const srcDim = getMediaDimensions(inputs.image);
      const modeStr = String(params.mode ?? 'fit');
      let modeInt = 1; // Fit
      if (modeStr === 'exact') modeInt = 0;
      else if (modeStr === 'fill') modeInt = 2;

      return {
        u_image: inputs.image as unknown as import('../../rendering/types').TextureHandle,
        u_srcResolution: [srcDim.width, srcDim.height],
        u_targetResolution: [dimensions.width, dimensions.height],
        u_mode: modeInt,
      };
    },
  }),
};
